import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { HeaderBar, useAuth } from '@screentone/addon-auth-wrapper';
import { Header, useHeader } from '@screentone/core';

import { Artifact, PublicationSetting } from 'data/generated/graphql';
import useHeaderObserver from 'hooks/header/useHeaderObserver';
import { usePublicationSettings } from 'hooks/publication-settings';
import { ARTIFACT_ROUTE_CONFIGS, RouteConfig } from 'utils/artifactRoutes';

// onPropertySwitch in OktaRouter (outside the Router context) cannot use useNavigate directly.
// HeaderBarWrapper lives inside the Router, so it keeps this ref updated for OktaRouter to consume.
export const navigateRef: { current: ((path: string) => void) | null } = { current: null };

interface UseHeader {
  closeDrawer: () => void;
}

const mobileSidebarLabel = 'Curation Navigation';

const createMobileSidebarComponentProps = (
  publicationSettings: PublicationSetting | undefined
): RouteConfig[] | undefined => {
  const allArtifacts = new Set(Object.values(Artifact));

  const enabledArtifacts = publicationSettings?.enabledArtifacts.filter((artifact) => allArtifacts.has(artifact));
  const componentProps = enabledArtifacts?.map((artifact) => ARTIFACT_ROUTE_CONFIGS[artifact]);

  return componentProps;
};

const HeaderBarWrapper = ({ children }: { children: React.ReactNode }) => {
  useHeaderObserver();

  const navigate = useNavigate();
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  const { currentProperty } = useAuth();
  const useHeaderHook = useHeader as () => UseHeader;
  const { closeDrawer } = useHeaderHook();
  const { data: publicationSettingsResp } = usePublicationSettings();

  const publicationSettings = publicationSettingsResp?.publicationSetting;
  const mobileSidebarComponentProps = createMobileSidebarComponentProps(publicationSettings);
  const createMobileSidebarComponent = () => (
    <Header.LinkGroup>
      {mobileSidebarComponentProps?.map(({ path, name }) => (
        <Header.Link
          key={name}
          componentEl={Link}
          to={`/${(currentProperty && currentProperty) ?? ''}${path}`}
          onClick={() => closeDrawer()}
        >
          {name}
        </Header.Link>
      ))}
    </Header.LinkGroup>
  );
  const mobileSidebar = currentProperty && {
    label: mobileSidebarLabel,
    component: createMobileSidebarComponent()
  };
  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <HeaderBar mobileSidebar={mobileSidebar}>{children}</HeaderBar>
  );
};

export default HeaderBarWrapper;
